import React from 'react';
import { Box, Container, Typography, Button, Chip, Grid } from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { landingColors } from '../../../theme/landingTheme';

interface HeroSectionProps {
  onRequestDemo: () => void;
}

export const HeroSection: React.FC<HeroSectionProps> = ({ onRequestDemo }) => {
  return (
    <Box
      component="section"
      sx={{
        position: 'relative',
        minHeight: { xs: 'auto', md: '100vh' },
        display: 'flex',
        alignItems: 'center',
        pt: { xs: 8, md: 0 },
        pb: { xs: 10, md: 0 },
        overflow: 'hidden',
        // Subtle gradient background
        background: `
          radial-gradient(ellipse at 20% 0%, rgba(99, 102, 241, 0.08) 0%, transparent 50%),
          radial-gradient(ellipse at 80% 100%, rgba(139, 92, 246, 0.06) 0%, transparent 50%),
          linear-gradient(180deg, ${landingColors.background} 0%, #FFFFFF 100%)
        `,
      }}
    >
      {/* Decorative Elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '15%',
          right: '10%',
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: `radial-gradient(circle, rgba(99, 102, 241, 0.05) 0%, transparent 70%)`,
          filter: 'blur(40px)',
          pointerEvents: 'none',
          display: { xs: 'none', lg: 'block' },
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '10%',
          left: '5%',
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: `radial-gradient(circle, rgba(139, 92, 246, 0.04) 0%, transparent 70%)`,
          filter: 'blur(30px)',
          pointerEvents: 'none',
          display: { xs: 'none', lg: 'block' },
        }}
      />

      {/* Grid pattern overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `
            linear-gradient(${landingColors.border}40 1px, transparent 1px),
            linear-gradient(90deg, ${landingColors.border}40 1px, transparent 1px)
          `,
          backgroundSize: '64px 64px',
          maskImage: 'radial-gradient(ellipse at center, black 20%, transparent 70%)',
          opacity: 0.5,
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
          {/* Left Content */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                maxWidth: 580,
                mx: { xs: 'auto', md: 0 },
                textAlign: { xs: 'center', md: 'left' },
              }}
            >
              {/* Badge */}
              <Chip
                label="NEW: B2B Portal Now Available"
                sx={{
                  mb: 3,
                  px: 1,
                  height: 32,
                  backgroundColor: landingColors.primaryMuted,
                  color: landingColors.primary,
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  letterSpacing: '0.02em',
                  border: `1px solid ${landingColors.primary}20`,
                  '& .MuiChip-label': {
                    px: 1,
                  },
                  animation: 'fadeInUp 0.6s ease-out',
                  '@keyframes fadeInUp': {
                    from: {
                      opacity: 0,
                      transform: 'translateY(20px)',
                    },
                    to: {
                      opacity: 1,
                      transform: 'translateY(0)',
                    },
                  },
                }}
              />

              {/* Headline */}
              <Typography
                variant="h1"
                sx={{
                  mb: 3,
                  animation: 'fadeInUp 0.6s ease-out 0.1s both',
                }}
              >
                Manage Your Store{' '}
                <Box
                  component="span"
                  sx={{
                    background: `linear-gradient(135deg, ${landingColors.primary} 0%, ${landingColors.secondary} 100%)`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Like a Pro
                </Box>
              </Typography>

              {/* Subheadline */}
              <Typography
                variant="subtitle1"
                sx={{
                  mb: 4,
                  fontSize: { xs: '1rem', md: '1.25rem' },
                  lineHeight: 1.7,
                  color: landingColors.textSecondary,
                  animation: 'fadeInUp 0.6s ease-out 0.2s both',
                }}
              >
                The complete platform for modern retail. Real-time inventory,
                powerful POS, smart analytics, and B2B capabilities—all in one
                beautiful dashboard.
              </Typography>

              {/* CTA Buttons */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                  justifyContent: { xs: 'center', md: 'flex-start' },
                  mb: 5,
                  animation: 'fadeInUp 0.6s ease-out 0.3s both',
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={onRequestDemo}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    background: `linear-gradient(135deg, ${landingColors.primary} 0%, ${landingColors.primaryDark} 100%)`,
                    boxShadow: '0 8px 32px rgba(99, 102, 241, 0.35)',
                    px: 4,
                    py: 1.75,
                    fontSize: '1rem',
                    '&:hover': {
                      boxShadow: '0 12px 40px rgba(99, 102, 241, 0.45)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  Request Demo
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<PlayArrowRoundedIcon />}
                  sx={{
                    borderColor: landingColors.border,
                    borderWidth: 2,
                    color: landingColors.textPrimary,
                    px: 4,
                    py: 1.75,
                    fontSize: '1rem',
                    '&:hover': {
                      borderWidth: 2,
                      borderColor: landingColors.primary,
                      backgroundColor: landingColors.primaryMuted,
                    },
                  }}
                >
                  Watch Video
                </Button>
              </Box>

              {/* Trust Indicators */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'center', md: 'flex-start' },
                  gap: { xs: 2, sm: 4 },
                  animation: 'fadeInUp 0.6s ease-out 0.4s both',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: landingColors.success,
                      animation: 'pulse 2s infinite',
                      '@keyframes pulse': {
                        '0%, 100%': {
                          opacity: 1,
                          transform: 'scale(1)',
                        },
                        '50%': {
                          opacity: 0.7,
                          transform: 'scale(1.1)',
                        },
                      },
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: landingColors.textMuted, fontWeight: 500 }}
                  >
                    500+ Stores Active
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: landingColors.primary,
                    }}
                  >
                    4.9/5
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: landingColors.textMuted, fontWeight: 500 }}
                  >
                    User Rating
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: landingColors.success,
                    }}
                  >
                    99.9%
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: landingColors.textMuted, fontWeight: 500 }}
                  >
                    Uptime
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Right Content - Dashboard Preview */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                position: 'relative',
                animation: 'fadeInUp 0.8s ease-out 0.3s both',
              }}
            >
              {/* Dashboard mockup */}
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: '20px',
                  overflow: 'hidden',
                  boxShadow: '0 40px 80px rgba(15, 23, 42, 0.15)',
                  border: `1px solid ${landingColors.border}`,
                  backgroundColor: '#0D0F12',
                  transform: { xs: 'none', md: 'perspective(1000px) rotateY(-5deg)' },
                  transition: 'transform 0.5s ease',
                  '&:hover': {
                    transform: { xs: 'none', md: 'perspective(1000px) rotateY(0deg)' },
                  },
                }}
              >
                {/* Browser Chrome */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1.5,
                    backgroundColor: '#151820',
                    borderBottom: '1px solid #282D3A',
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 0.75 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: '#EF4444',
                      }}
                    />
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: '#F59E0B',
                      }}
                    />
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: '#10B981',
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      mx: 4,
                      py: 0.5,
                      px: 2,
                      backgroundColor: '#1C2029',
                      borderRadius: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        backgroundColor: '#10B981',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          backgroundColor: '#fff',
                          borderRadius: '50%',
                        }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: '0.75rem',
                        color: '#64748B',
                        fontFamily: '"JetBrains Mono", monospace',
                      }}
                    >
                      app.nexera.com
                    </Typography>
                  </Box>
                </Box>

                {/* Dashboard Content */}
                <Box sx={{ p: 3 }}>
                  {/* Header */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 3,
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          color: '#F1F5F9',
                          fontWeight: 700,
                          fontSize: '1.25rem',
                          fontFamily: '"Cabinet Grotesk", sans-serif',
                        }}
                      >
                        Dashboard
                      </Typography>
                      <Typography
                        sx={{
                          color: '#64748B',
                          fontSize: '0.8rem',
                        }}
                      >
                        Welcome back, Sarah
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        px: 2,
                        py: 0.75,
                        backgroundColor: 'rgba(16, 185, 129, 0.1)',
                        borderRadius: '8px',
                        border: '1px solid rgba(16, 185, 129, 0.2)',
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#10B981',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          fontFamily: '"JetBrains Mono", monospace',
                        }}
                      >
                        LIVE
                      </Typography>
                    </Box>
                  </Box>

                  {/* Stats Grid */}
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {[
                      { label: 'Today Sales', value: '$12,847', change: '+12%', color: '#6366F1' },
                      { label: 'Orders', value: '284', change: '+8%', color: '#10B981' },
                      { label: 'Inventory', value: '1,247', change: '', color: '#F59E0B' },
                    ].map((stat, i) => (
                      <Grid item xs={4} key={i}>
                        <Box
                          sx={{
                            p: 2,
                            backgroundColor: '#151820',
                            borderRadius: '12px',
                            border: '1px solid #282D3A',
                          }}
                        >
                          <Typography
                            sx={{
                              color: '#64748B',
                              fontSize: '0.7rem',
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                              mb: 0.5,
                            }}
                          >
                            {stat.label}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                            <Typography
                              sx={{
                                color: '#F1F5F9',
                                fontWeight: 700,
                                fontSize: '1.1rem',
                                fontFamily: '"JetBrains Mono", monospace',
                              }}
                            >
                              {stat.value}
                            </Typography>
                            {stat.change && (
                              <Typography
                                sx={{
                                  color: '#10B981',
                                  fontSize: '0.7rem',
                                  fontWeight: 600,
                                }}
                              >
                                {stat.change}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Chart placeholder */}
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: '#151820',
                      borderRadius: '12px',
                      border: '1px solid #282D3A',
                      height: 120,
                      display: 'flex',
                      alignItems: 'flex-end',
                      gap: 0.5,
                    }}
                  >
                    {[40, 65, 45, 80, 55, 90, 70, 85, 60, 95, 75, 88].map((h, i) => (
                      <Box
                        key={i}
                        sx={{
                          flex: 1,
                          height: `${h}%`,
                          borderRadius: '4px 4px 0 0',
                          background: i === 11
                            ? `linear-gradient(180deg, ${landingColors.primary} 0%, ${landingColors.primaryDark} 100%)`
                            : 'rgba(99, 102, 241, 0.2)',
                          transition: 'all 0.3s ease',
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </Box>

              {/* Floating notification */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '20%',
                  left: { xs: '5%', md: '-10%' },
                  p: 2,
                  backgroundColor: '#fff',
                  borderRadius: '12px',
                  boxShadow: '0 8px 32px rgba(15, 23, 42, 0.12)',
                  border: `1px solid ${landingColors.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  animation: 'float 4s ease-in-out infinite',
                  '@keyframes float': {
                    '0%, 100%': { transform: 'translateY(0)' },
                    '50%': { transform: 'translateY(-10px)' },
                  },
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '10px',
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography sx={{ fontSize: '1.25rem' }}>+</Typography>
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: landingColors.textPrimary,
                    }}
                  >
                    New Sale
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: landingColors.success,
                      fontWeight: 600,
                    }}
                  >
                    +$249.00
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default HeroSection;
